import React, { forwardRef, useContext, useMemo } from 'react';
import styled from 'styled-components';

import ParentsPanel from './ParentsPanel';
import { SidekickThreadEditorContext } from 'src/presentation/components/pages/sidekick/threads/SidekickThreadEditorContext';
import useOpenThread from 'src/presentation/components/pages/sidekick/threads/hooks/useOpenThread';
import useSidekickThreads from '../../hooks/useSidekickThreads';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { getThreadsHierarchy } from '../../actions/moduleHelper';
import { THREAD_CANVAS_WIDTH } from '../../SidekickConstants';

type ThreadParentsPanelProps = {
  threadId: string;
  setIsParentsLoading: (value: boolean) => void;
  parentItemId: string;
};

const ThreadParentsPanel = forwardRef(
  ({ threadId, setIsParentsLoading, parentItemId }: ThreadParentsPanelProps, ref) => {
    const { root } = useContext(SidekickThreadEditorContext);

    const { items, loadingState } = useSidekickThreads();

    const { handleOpenThread } = useOpenThread({ root });

    const handleOnClick = (threadId: string) => {
      handleOpenThread(threadId);
    };

    const parents: any = useMemo(() => {
      if (loadingState === 'succeeded') {
        const threadsHierarchy = getThreadsHierarchy(items);

        const parentIds = Object.keys(threadsHierarchy).filter(
          parentId => threadsHierarchy[parentId] && threadsHierarchy[parentId].includes(threadId)
        );

        return items
          .filter(thread => parentIds.includes(String(thread.id)))
          .map(thread => ({ id: thread.id, name: thread.name }));
      }
      return {};
    }, [items, loadingState, threadId]);

    return (
      <StyledContainer ref={ref}>
        {isLoadingStatus(loadingState) ? (
          <>Loading...</>
        ) : (
          <ParentsPanel
            parentItemId={parentItemId}
            onItemClick={handleOnClick}
            parents={parents}
            setIsParentsLoading={setIsParentsLoading}
          />
        )}
      </StyledContainer>
    );
  }
);

export const StyledContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: center;
  min-width: ${THREAD_CANVAS_WIDTH}px;

  position: absolute;
  top: 80px;
  width: 100%;
  z-index: 3;
`;

export default ThreadParentsPanel;
